import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Lock, CreditCard, Truck } from 'lucide-react';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const [step, setStep] = useState('shipping'); // shipping, payment, processing, success
  const [checkoutId, setCheckoutId] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [shippingInfo, setShippingInfo] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Switzerland',
    notes: ''
  });

  const apiUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Redirect if cart is empty
  useEffect(() => {
    if (cartItems.length === 0 && step === 'shipping') {
      navigate('/cart');
    }
  }, [cartItems, navigate, step]);

  // Load SumUp SDK
  useEffect(() => {
    if (step === 'payment' && !document.querySelector('script[src*="sumup"]')) {
      const script = document.createElement('script');
      script.src = 'https://gateway.sumup.com/gateway/ecom/card/v2/sdk.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, [step]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShippingInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateShippingInfo = () => {
    const required = ['full_name', 'email', 'phone', 'address', 'city', 'postal_code'];
    for (const field of required) {
      if (!shippingInfo[field]?.trim()) {
        setError(`Please fill in ${field.replace('_', ' ')}`);
        return false;
      }
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(shippingInfo.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    return true;
  };

  const handleSubmitShipping = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateShippingInfo()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare checkout data
      const checkoutData = {
        items: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image || item.images?.[0] || '/placeholder.png',
          category: item.category || 'Product',
          selectedSize: item.selectedSize || null
        })),
        shipping_info: shippingInfo,
        total_amount: getCartTotal()
      };

      // Create checkout on backend
      const response = await fetch(`${apiUrl}/api/checkout/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkoutData)
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout');
      }

      const data = await response.json();
      setCheckoutId(data.checkout_id);
      setOrderId(data.order_id);
      
      // Move to payment step
      setStep('payment');
      
      // Mount SumUp widget after a short delay
      setTimeout(() => {
        mountSumUpWidget(data.checkout_id);
      }, 500);

    } catch (err) {
      setError(err.message || 'Failed to process checkout');
    } finally {
      setLoading(false);
    }
  };

  const mountSumUpWidget = (checkoutId) => {
    if (window.SumUpCard) {
      try {
        window.SumUpCard.mount({
          id: 'sumup-card',
          checkoutId: checkoutId,
          onResponse: (type, body) => {
            if (type === 'success') {
              setStep('processing');
              checkPaymentStatus(checkoutId);
            } else if (type === 'error') {
              setError(body?.message || 'Payment failed. Please try again.');
              setStep('payment');
            }
          },
        });
      } catch (err) {
        setError('Failed to load payment widget. Please refresh and try again.');
      }
    } else {
      setError('Payment system not available. Please refresh the page.');
    }
  };

  const checkPaymentStatus = async (checkoutId) => {
    try {
      // Poll payment status
      const maxAttempts = 10;
      let attempts = 0;

      const pollStatus = async () => {
        attempts++;
        
        const response = await fetch(`${apiUrl}/api/checkout/checkout/${checkoutId}/status`);
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.status === 'PAID') {
            setStep('success');
            clearCart();
            return;
          } else if (data.status === 'FAILED') {
            setError('Payment was declined. Please try again.');
            setStep('payment');
            return;
          }
        }

        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          setError('Payment verification timed out. Please contact support with order ID: ' + orderId);
          setStep('success'); // Still show success page
        }
      };

      pollStatus();
    } catch (err) {
      console.error('Error checking payment status:', err);
      setStep('success'); // Show success page anyway
    }
  };

  // Shipping Form Step
  if (step === 'shipping') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Checkout</h1>
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-2 text-[hsl(45,100%,70%)] font-bold">
                <Truck className="w-4 h-4" /> Shipping Information
              </span>
              <span className="text-gray-400">→</span>
              <span className="flex items-center gap-2 text-gray-400">
                <CreditCard className="w-4 h-4" /> Payment
              </span>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Shipping Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Shipping Details</h2>
                
                <form onSubmit={handleSubmitShipping} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      value={shippingInfo.full_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(45,100%,70%)] focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={shippingInfo.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(45,100%,70%)] focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={shippingInfo.phone}
                        onChange={handleInputChange}
                        placeholder="+41 79 123 45 67"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(45,100%,70%)] focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Street Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={shippingInfo.address}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(45,100%,70%)] focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={shippingInfo.city}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(45,100%,70%)] focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Postal Code *
                      </label>
                      <input
                        type="text"
                        name="postal_code"
                        value={shippingInfo.postal_code}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(45,100%,70%)] focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Country *
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={shippingInfo.country}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(45,100%,70%)] focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Order Notes (Optional)
                    </label>
                    <textarea
                      name="notes"
                      value={shippingInfo.notes}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[hsl(45,100%,70%)] focus:border-transparent"
                      placeholder="Any special instructions for your order?"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[hsl(45,100%,70%)] hover:bg-[hsl(45,100%,60%)] disabled:bg-gray-300 text-black font-bold py-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      'Processing...'
                    ) : (
                      <>
                        Continue to Payment
                        <Lock className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Order Summary</h2>
                
                <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex gap-3 text-sm">
                      <div className="w-16 h-16 bg-gray-100 rounded flex-shrink-0 overflow-hidden">
                        <img
                          src={item.image || item.images?.[0] || '/placeholder.png'}
                          alt={item.name || 'Product'}
                          className="w-full h-full object-cover rounded"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/placeholder.png';
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{item.name}</div>
                        {item.selectedSize && (
                          <div className="text-gray-400 text-xs">Size: {item.selectedSize}</div>
                        )}
                        <div className="text-gray-500">Qty: {item.quantity}</div>
                        <div className="text-[hsl(45,100%,70%)] font-bold">{item.price}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4 space-y-3">
                  <div className="flex justify-between text-sm text-gray-700">
                    <span>Subtotal</span>
                    <span>CHF {getCartTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-700">
                    <span>Shipping</span>
                    <span className="text-green-600 font-semibold">Included</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span className="text-[hsl(45,100%,70%)]">CHF {getCartTotal().toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-6 text-xs text-gray-500 space-y-2">
                  <p className="flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Secure checkout
                  </p>
                  <p>✓ Handcrafted with care</p>
                  <p>✓ Free worldwide shipping</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Payment Step
  if (step === 'payment') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Payment</h1>
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-2 text-green-600">
                <Truck className="w-4 h-4" /> Shipping ✓
              </span>
              <span className="text-gray-400">→</span>
              <span className="flex items-center gap-2 text-[hsl(45,100%,70%)] font-bold">
                <CreditCard className="w-4 h-4" /> Payment
              </span>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Secure Payment</h2>
              <p className="text-gray-600">Order ID: <span className="font-mono font-bold">{orderId}</span></p>
              <p className="text-2xl font-bold text-[hsl(45,100%,70%)] mt-2">CHF {getCartTotal().toFixed(2)}</p>
            </div>

            {/* SumUp Payment Widget will mount here */}
            <div id="sumup-card" className="mb-6" />

            <div className="text-sm text-gray-500 text-center">
              <Lock className="w-4 h-4 inline mr-1" />
              Your payment is secured by SumUp
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Processing Step
  if (step === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[hsl(45,100%,70%)] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment...</h2>
          <p className="text-gray-600">Please wait while we confirm your payment</p>
        </div>
      </div>
    );
  }

  // Success Step
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-4">Order Confirmed!</h1>
            <p className="text-xl text-gray-600 mb-2">Thank you for your purchase</p>
            <p className="text-2xl font-bold text-[hsl(45,100%,70%)] mb-8">Order #{orderId}</p>

            <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
              <h3 className="font-bold text-gray-900 mb-3">What happens next?</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>You'll receive a confirmation email at <strong>{shippingInfo.email}</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>Our team will prepare your handcrafted pieces with care</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>You'll receive tracking information once shipped</span>
                </li>
              </ul>
            </div>

            <div className="bg-[hsl(45,100%,70%)]/10 rounded-lg p-6 mb-8">
              <p className="text-gray-800 italic">
                "Thank you for choosing Artourius Design! Each piece in our collection is meticulously 
                handcrafted with love and attention to detail. We appreciate your trust in our artistry."
              </p>
              <div className="mt-4 text-sm text-gray-600">
                <p>Questions? Contact us:</p>
                <p className="font-semibold">sales@artourius.design</p>
                <p className="font-semibold">+41 79 900 30 30</p>
              </div>
            </div>

            <button
              onClick={() => navigate('/')}
              className="bg-[hsl(45,100%,70%)] hover:bg-[hsl(45,100%,60%)] text-black font-bold px-8 py-3 rounded-lg transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CheckoutPage;
